import type { FC } from 'react';
import {
  FileSpreadsheet,
  Table,
  Filter,
  ArrowUpDown,
  Calculator,
  Copy,
  BarChart3,
  LayoutGrid,
  Merge,
  Layers,
  ArrowLeftRight,
  Download,
  Trash2,
  QrCode,
  ChevronDown,
  Grid3X3,
} from 'lucide-react';

export type IconProps = {
  className?: string;
};

export type NodeIconComponent = FC<IconProps>;

export interface NodeMeta {
  /** 节点标签 */
  label: string;
  /** 节点描述 */
  description: string;
  icon: NodeIconComponent;
  color: string;
  bg: string;
  border: string;
  /** Maximum number of input connections allowed. undefined means unlimited */
  maxInputs?: number;
}

export const NODE_META: Record<string, NodeMeta> = {
  // 数据源节点：无输入
  load_excel:   { label: 'Excel',        description: '从Excel文件加载数据',      icon: FileSpreadsheet, color: 'text-blue-600',   bg: 'bg-blue-50',   border: 'border-blue-200' },
  load_csv:     { label: 'CSV',          description: '从CSV文件加载数据',        icon: Table,          color: 'text-sky-600',    bg: 'bg-sky-50',    border: 'border-sky-200' },
  load_table:   { label: '表格',         description: '从表格加载数据',           icon: Table,          color: 'text-cyan-600',   bg: 'bg-cyan-50',   border: 'border-cyan-200' },
  
  // 单输入节点
  filter:       { label: '筛选',         description: '筛选数据行',               icon: Filter,         color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200',  maxInputs: 1 },
  sort:         { label: '排序',         description: '按列排序数据',             icon: ArrowUpDown,    color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200',  maxInputs: 1 },
  compute:      { label: '计算',         description: '添加计算列',               icon: Calculator,     color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200',  maxInputs: 1 },
  transform:    { label: '转换',         description: '数据转换',                 icon: ArrowUpDown,    color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200',  maxInputs: 1 },
  dedup:        { label: '去重',         description: '删除重复行',               icon: Copy,           color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200',  maxInputs: 1 },
  drop:         { label: '删除列',       description: '删除指定列',               icon: Trash2,         color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200',  maxInputs: 1 },
  aggregate:    { label: '聚合',         description: '数据聚合统计',             icon: BarChart3,      color: 'text-rose-600',   bg: 'bg-rose-50',   border: 'border-rose-200', maxInputs: 1 },
  group_by:     { label: '分组',         description: '按列分组',                 icon: LayoutGrid,     color: 'text-pink-600',   bg: 'bg-pink-50',   border: 'border-pink-200', maxInputs: 1 },
  barcode:      { label: '条码',         description: '生成条码',                 icon: QrCode,         color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', maxInputs: 1 },
  top_n:        { label: 'Top N',        description: '取前N行',                  icon: ChevronDown,    color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200',  maxInputs: 1 },
  pivot:        { label: '透视',         description: '数据透视表',               icon: Grid3X3,        color: 'text-rose-600',   bg: 'bg-rose-50',   border: 'border-rose-200', maxInputs: 1 },
  
  // 多输入节点
  join:         { label: '关联',         description: '多表关联',                 icon: Merge,          color: 'text-amber-600',  bg: 'bg-amber-50',  border: 'border-amber-200' },
  append:       { label: '追加',         description: '追加数据',                 icon: Layers,         color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
  diff:         { label: '对比',         description: '数据对比',                 icon: ArrowLeftRight, color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200' },
  
  // 输出节点
  export_excel: { label: '导出Excel',    description: '导出为Excel文件',          icon: Download,       color: 'text-zinc-600',   bg: 'bg-zinc-100',  border: 'border-zinc-200', maxInputs: 1 },
  export_csv:   { label: '导出CSV',      description: '导出为CSV文件',            icon: Download,       color: 'text-zinc-500',   bg: 'bg-zinc-50',   border: 'border-zinc-200', maxInputs: 1 },
};

/** Multi-input nodes: can accept unlimited inputs */
export const MULTI_INPUT_TYPES = new Set(['join', 'append', 'diff']);

/** Source-only nodes: no incoming connections */
export const INPUT_TYPES = new Set(['load_excel', 'load_csv', 'load_table', 'load_api', 'online_sheet']);

/** Sink-only nodes: no outgoing connections */
export const OUTPUT_TYPES = new Set(['export_excel', 'export_csv']);

/** Returns a hex color string for node coloring */
export function getNodeColor(type: string): string {
  const colorMap: Record<string, string> = {
    load_excel: '#bfdbfe', load_csv: '#bae6fd', load_table: '#a5f3fc',
    filter: '#d9f99d', sort: '#d9f99d', compute: '#d9f99d', transform: '#d9f99d', 
    dedup: '#d9f99d', drop: '#d9f99d', top_n: '#d9f99d', barcode: '#e9d5ff',
    aggregate: '#fecdd3', group_by: '#fbcfe8', pivot: '#fecdd3',
    join: '#fde68a', append: '#fed7aa',
    diff: '#ddd6fe',
    export_excel: '#e4e4e7', export_csv: '#f4f4f5',
  };
  return colorMap[type] ?? '#e4e4e7';
}
